import { useRef } from 'react';
import { graphql, navigate } from 'gatsby';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useInternalLinks from '@/hooks/useInternalLinks';
import countryList from '@/components/Forms/constants/countryList';
import {
  StyledSERPForm,
  StyledFormHeadline,
  StyledFormFooter,
  StyledButton,
  StyledForm,
} from './SERPForm.styles';
import { zodResolver } from '@hookform/resolvers/zod';
import Input from '../Forms/components/Input/Input';
import Select from '../Forms/components/Select/Select';
import Textarea from '../Forms/components/Textarea/Textarea';
import FormError from '../Forms/components/FormError/FormError';
import errorLogger from '@/utils/errorLogger';
import pushInDataLayer from '@/utils/pushInDataLayer';
import { type ReCAPTCHA } from 'react-google-recaptcha';
import GoogleRecaptcha from '../Forms/components/GoogleRecaptcha/GoogleRecaptcha';
import { isTokenRecaptchaValid } from '@/utils/validateGoogleRecaptchaToken';
import LegalText, { type LegalTextProps } from '../Forms/components/LegalText/LegalText';
import getCheqHiddenFieldValue from '../Forms/components/CheqHiddenField/getCheqHiddenFieldValue';
import CheqHiddenField from '../Forms/components/CheqHiddenField/CheqHiddenField';
import MarketingOptOut from '../Forms/components/MarketingOptOut/MarketingOptOut';
import { serpFormSchema } from './validation';
import { useLocation } from '@reach/router';
import { type BlockHTMLId } from '@/utils/renderBlock';
import useSetGeolocatedCountry from '../Forms/hooks/useSetGeolocatedCountry';
import useFormComplete from '../Forms/hooks/useFormComplete';
import getSalesForceId from '@/utils/getSalesForceId';

const SERPForm = ({
  serpFormElements: elements,
  serpFormSystem: { codename },
}: Queries.SERP_FORMFragment & BlockHTMLId) => {
  const { headline, submit_button_title, campaign_id, legal_text, success_redirect_page, options } =
    elements;
  const { t } = useTranslation();
  const getInternalLinkURLByCodename = useInternalLinks();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const hideLabel = options?.value?.includes('hide_labels') ?? false;
  const { pathname } = useLocation();
  const leadCampaignId = getSalesForceId();

  const {
    register,
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
    setValue,
  } = useForm({
    mode: 'onBlur',
    resolver: zodResolver(serpFormSchema),
    shouldUnregister: true,
    defaultValues: {
      campaign_id: leadCampaignId ?? campaign_id.value,
      form_type: 'serp',
      first_name: '',
      last_name: '',
      title: '',
      academic_email: '',
      country: null,
      application_for: 'research_grants',
      university_lab_affiliation: '',
      research_purpose: '',
      useful_links: '',
      university_course_name: '',
      number_of_students: null,
      faculty_directory_link: '',
    },
  });

  useSetGeolocatedCountry({ setValue, field: 'country', countryList });
  const isFormCompleteReady = useFormComplete({
    setValue,
    selectMappings: {
      country: countryList,
    },
  });
  const enhanceFormComplete = options?.value?.includes('enhance_form_complete') ?? false;
  const shouldHideFields = enhanceFormComplete && !isFormCompleteReady;

  const redirectURL =
    getInternalLinkURLByCodename(success_redirect_page.value.system.codename) ?? '/';

  const onSubmit = handleSubmit(async (formValues) => {
    const token = await recaptchaRef.current?.executeAsync();
    await isTokenRecaptchaValid(token ?? null, 'serp-form');

    try {
      const cq_req_id = getCheqHiddenFieldValue();

      const requestBody = {
        ...formValues,
        cq_req_id, // Cheq Request ID
      };

      const response = await fetch('/api/submit-form', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      pushInDataLayer({
        event: 'submittedForm',
        submittedFormData: {
          type: 'serp',
          email: requestBody.academic_email,
          country: requestBody.country,
          fromPage: pathname,
          campaignId: leadCampaignId ?? requestBody.campaign_id,
        },
      });

      await new Promise((resolve) => setTimeout(resolve, 1000));

      navigate(redirectURL);
    } catch (error) {
      errorLogger(error);
      setError('root', { type: 'custom', message: t('form.Could not submit form') });
    }
  });

  const academicEmailInput = (
    <Input
      type="email"
      id="academic_email"
      required
      placeholder={t('form.Academic Email')}
      formGroupProps={{ label: t('form.Academic Email'), hideLabel }}
      {...register('academic_email')}
      errors={errors}
      disabled={isSubmitting}
    />
  );

  return (
    <StyledSERPForm data-codename={codename} id={codename}>
      <StyledFormHeadline as="h3">{headline.value}</StyledFormHeadline>
      <StyledForm noValidate onSubmit={onSubmit} id={codename}>
        {enhanceFormComplete && academicEmailInput}
        <Input
          type="text"
          id="first_name"
          required
          placeholder={t('form.First Name')}
          formGroupProps={{ label: t('form.First Name'), hideLabel, isHidden: shouldHideFields }}
          {...register('first_name')}
          errors={errors}
          disabled={isSubmitting}
        />
        <Input
          type="text"
          id="last_name"
          required
          placeholder={t('form.Last Name')}
          formGroupProps={{ label: t('form.Last Name'), hideLabel, isHidden: shouldHideFields }}
          {...register('last_name')}
          errors={errors}
          disabled={isSubmitting}
        />
        <Input
          type="text"
          id="title"
          required
          placeholder={t('form.Title')}
          formGroupProps={{ label: t('form.Title'), hideLabel, isHidden: shouldHideFields }}
          {...register('title')}
          errors={errors}
          disabled={isSubmitting}
        />
        {!enhanceFormComplete && academicEmailInput}
        <Controller
          control={control}
          name="country"
          render={({ field }) => (
            <Select
              errors={errors}
              required
              id="country"
              placeholder={t('form.Select a country')}
              options={countryList}
              formGroupProps={{ label: 'Country', hideLabel }}
              isDisabled={isSubmitting}
              {...field}
            />
          )}
        />
        <input type="hidden" {...register('application_for')} />

        <Input
          type="text"
          id="university_lab_affiliation"
          required
          placeholder={t('form.University and Lab affiliation')}
          formGroupProps={{ label: t('form.University and Lab affiliation'), hideLabel }}
          {...register('university_lab_affiliation')}
          errors={errors}
          disabled={isSubmitting}
        />
        <Textarea
          id="research_purpose"
          required
          placeholder={t('form.Tell us more about the nature of your project')}
          formGroupProps={{ label: t('form.Research purpose'), hideLabel }}
          {...register('research_purpose')}
          errors={errors}
          disabled={isSubmitting}
        />
        <Input
          type="text"
          id="useful_links"
          placeholder={t('form.Accreditation, Program page etc.')}
          formGroupProps={{ label: t('form.Useful links'), hideLabel }}
          {...register('useful_links')}
          errors={errors}
          disabled={isSubmitting}
        />
        <MarketingOptOut
          label={t('form.Marketing Emails Opt Out')}
          errors={errors}
          register={register}
          disabled={isSubmitting}
        />
        <CheqHiddenField />
        <input type="hidden" {...register('form_type')} />
        <input type="hidden" {...register('campaign_id')} />
        <StyledFormFooter>
          <GoogleRecaptcha ref={recaptchaRef} />
          <StyledButton type="submit" variant="primary" isLoading={isSubmitting}>
            {submit_button_title.value}
          </StyledButton>
          <LegalText richText={legal_text as LegalTextProps['richText']} />
          {errors?.root?.message && <FormError>{errors.root.message}</FormError>}
        </StyledFormFooter>
      </StyledForm>
    </StyledSERPForm>
  );
};

export const fragment = graphql`
  fragment SERP_FORM on kontent_item_serp_form {
    __typename
    serpFormElements: elements {
      headline {
        value
      }
      submit_button_title {
        value
      }
      campaign_id {
        value
      }
      legal_text {
        value
        links {
          link_id
          codename
        }
      }
      success_redirect_page {
        value {
          system {
            codename
          }
        }
      }
      options {
        value
      }
    }
    serpFormSystem: system {
      id
      codename
    }
  }
`;

export default SERPForm;
